// The handful of elements every page is built from, and the icons they use.
import { useEffect, useState, type MouseEvent, type ReactNode } from 'react'
import { navigate } from '../router'
import { useTheme } from '../theme'

// ---------------------------------------------------------------------------- navigation

/** An in-app link: a real href for the browser, a pushState for the router. Modified and middle clicks are left to the browser. */
export function Link({ to, replace, className, title, ariaLabel, children }: { to: string; replace?: boolean; className?: string; title?: string; ariaLabel?: string; children: ReactNode }) {
  function onClick(e: MouseEvent<HTMLAnchorElement>) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
    e.preventDefault()
    navigate(to, replace)
  }
  return (
    <a href={to} className={className} title={title} aria-label={ariaLabel} onClick={onClick}>
      {children}
    </a>
  )
}

// ---------------------------------------------------------------------------- icons

/** Line icons after Feather (MIT), inlined so the app carries no icon library. */
export type IconName =
  | 'sun'
  | 'moon'
  | 'key'
  | 'copy'
  | 'check'
  | 'arrow-left'
  | 'chevron-right'
  | 'shield'
  | 'server'
  | 'users'
  | 'user'
  | 'plus'
  | 'phone'
  | 'external'
  | 'info'
  | 'globe'
  | 'log-out'
  | 'file'

const icons: Record<IconName, ReactNode> = {
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="2" x2="12" y2="4" />
      <line x1="12" y1="20" x2="12" y2="22" />
      <line x1="4.93" y1="4.93" x2="6.34" y2="6.34" />
      <line x1="17.66" y1="17.66" x2="19.07" y2="19.07" />
      <line x1="2" y1="12" x2="4" y2="12" />
      <line x1="20" y1="12" x2="22" y2="12" />
      <line x1="4.93" y1="19.07" x2="6.34" y2="17.66" />
      <line x1="17.66" y1="6.34" x2="19.07" y2="4.93" />
    </>
  ),
  moon: <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />,
  key: <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 1 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />,
  copy: (
    <>
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </>
  ),
  check: <polyline points="20 6 9 17 4 12" />,
  'arrow-left': (
    <>
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </>
  ),
  'chevron-right': <polyline points="9 18 15 12 9 6" />,
  shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
  server: (
    <>
      <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
      <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
      <line x1="6" y1="6" x2="6.01" y2="6" />
      <line x1="6" y1="18" x2="6.01" y2="18" />
    </>
  ),
  users: (
    <>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  user: (
    <>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </>
  ),
  plus: (
    <>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </>
  ),
  phone: (
    <>
      <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
      <line x1="12" y1="18" x2="12.01" y2="18" />
    </>
  ),
  external: (
    <>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </>
  ),
  'log-out': (
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </>
  ),
  file: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </>
  ),
}

export function Icon({ name, size = 18, className }: { name: IconName; size?: number; className?: string }) {
  return (
    <svg
      className={'icon' + (className ? ' ' + className : '')}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {icons[name]}
    </svg>
  )
}

/** Sun or moon: flips between the light and dark theme. */
export function ThemeToggle({ className }: { className?: string }) {
  const [theme, toggle] = useTheme()
  const dark = theme === 'dark'
  const label = dark ? 'Switch to light theme' : 'Switch to dark theme'
  return (
    <button type="button" className={'icon-button' + (className ? ' ' + className : '')} onClick={toggle} title={label} aria-label={label}>
      <Icon name={dark ? 'sun' : 'moon'} />
    </button>
  )
}

// ---------------------------------------------------------------------------- structure

/** The top of a page: an optional crumb above, the title with badges beside it, a lead under it, and actions on the right. */
export function PageHeader({ crumb, title, lead, aside, actions }: { crumb?: ReactNode; title: ReactNode; lead?: ReactNode; aside?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="page-head">
      <div className="page-head-main">
        {crumb}
        <div className="title-row">
          {typeof title === 'string' ? <h1>{title}</h1> : title}
          {aside}
        </div>
        {lead && <p className="lead">{lead}</p>}
      </div>
      {actions && <div className="page-head-actions">{actions}</div>}
    </div>
  )
}

export function Card({ title, lead, actions, children, className }: { title?: ReactNode; lead?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={'card' + (className ? ' ' + className : '')}>
      {(title || actions) && (
        <header className="card-head">
          <div className="card-head-text">
            {title && <h2>{title}</h2>}
            {lead && <p className="muted card-lead">{lead}</p>}
          </div>
          {actions && <div className="card-actions">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  )
}

/** A row of figures that sum a page up at a glance. */
export function Stats({ children }: { children: ReactNode }) {
  return <div className="stats">{children}</div>
}

export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: ReactNode }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {hint && <div className="stat-hint">{hint}</div>}
    </div>
  )
}

export interface Fact {
  label: string
  value: ReactNode
}

/** Label-and-value pairs in a grid; pairs without a value are left out. */
export function Facts({ items, className }: { items: Fact[]; className?: string }) {
  const shown = items.filter((f) => f.value !== null && f.value !== undefined && f.value !== '')
  if (shown.length === 0) return null
  return (
    <dl className={'facts' + (className ? ' ' + className : '')}>
      {shown.map((f) => (
        <div key={f.label}>
          <dt>{f.label}</dt>
          <dd>{f.value}</dd>
        </div>
      ))}
    </dl>
  )
}

/** Numbered steps, for explaining how something is done. */
export function Steps({ steps }: { steps: { title: string; text: ReactNode }[] }) {
  return (
    <ol className="steps">
      {steps.map((s) => (
        <li key={s.title}>
          <strong>{s.title}</strong>
          <span>{s.text}</span>
        </li>
      ))}
    </ol>
  )
}

export function EmptyState({ icon, title, children, action }: { icon: IconName; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <span className="empty-icon">
        <Icon name={icon} size={22} />
      </span>
      <h2>{title}</h2>
      {children && <p>{children}</p>}
      {action && <div className="row">{action}</div>}
    </div>
  )
}

// ---------------------------------------------------------------------------- feedback

export function ErrorText({ error }: { error: string | null | undefined }) {
  return error ? (
    <div className="error" role="alert">
      <Icon name="info" size={16} />
      <span>{error}</span>
    </div>
  ) : null
}

export function Notice({ children, kind = 'info' }: { children: ReactNode; kind?: 'info' | 'success' | 'warn' }) {
  return (
    <div className={'notice notice-' + kind}>
      <Icon name={kind === 'success' ? 'check' : kind === 'warn' ? 'shield' : 'info'} size={16} />
      <div>{children}</div>
    </div>
  )
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: ReactNode }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  )
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="spinner" role="status">
      <span className="spinner-dot" />
      {label && <span>{label}</span>}
    </div>
  )
}

export function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'good' | 'bad' | 'warn' | 'accent' }) {
  return <span className={'badge badge-' + tone}>{children}</span>
}

// ---------------------------------------------------------------------------- copying

/** Copies `value` to the clipboard and says so for a moment. */
export function CopyButton({ value, label = 'Copy', className }: { value: string; label?: string; className?: string }) {
  const [copied, setCopied] = useState(false)
  useEffect(() => {
    if (!copied) return
    const timer = window.setTimeout(() => setCopied(false), 1500)
    return () => window.clearTimeout(timer)
  }, [copied])
  async function copy() {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
    } catch {
      // clipboard blocked: the value is selectable, so nothing more to do
    }
  }
  return (
    <button type="button" className={'button button-small' + (copied ? ' copied' : '') + (className ? ' ' + className : '')} onClick={copy}>
      <Icon name={copied ? 'check' : 'copy'} size={14} />
      {copied ? 'Copied' : label}
    </button>
  )
}

/** A value shown in monospace with a copy button, for keys the user has to carry somewhere else. */
export function Copyable({ value, label }: { value: string; label?: string }) {
  return (
    <span className="copyable">
      {label && <span className="copyable-label">{label}</span>}
      <code className="copyable-value">{value}</code>
      <CopyButton value={value} />
    </span>
  )
}

/** A fragment of relatude.db.json, coloured like code, with one button that copies the whole thing. */
export function Snippet({ title, lines }: { title: string; lines: { key: string; value: string }[] }) {
  const text = '{\n' + lines.map((l, i) => `  "${l.key}": "${l.value}"${i < lines.length - 1 ? ',' : ''}`).join('\n') + '\n}'
  return (
    <div className="snippet">
      <div className="snippet-head">
        <span>
          <Icon name="file" size={14} /> {title}
        </span>
        <CopyButton value={text} />
      </div>
      <pre>
        <code>
          {'{\n'}
          {lines.map((l, i) => (
            <span key={l.key}>
              {'  '}
              <span className="snippet-key">"{l.key}"</span>: <span className="snippet-value">"{l.value}"</span>
              {i < lines.length - 1 ? ',' : ''}
              {'\n'}
            </span>
          ))}
          {'}'}
        </code>
      </pre>
    </div>
  )
}

// ---------------------------------------------------------------------------- providers

export function ProviderIcon({ provider }: { provider: string }) {
  switch (provider) {
    case 'google':
      return (
        <svg className="provider-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.4h6.5c-.3 1.5-1.1 2.8-2.4 3.6v3h3.9c2.3-2.1 3.5-5.2 3.5-8.7z" />
          <path fill="#34A853" d="M12 24c3.2 0 6-1.1 7.9-2.9l-3.9-3c-1.1.7-2.4 1.2-4 1.2-3.1 0-5.7-2.1-6.7-4.9H1.4v3.1C3.4 21.4 7.4 24 12 24z" />
          <path fill="#FBBC05" d="M5.3 14.4c-.2-.7-.4-1.5-.4-2.4s.1-1.6.4-2.4V6.5H1.4C.5 8.2 0 10 0 12s.5 3.8 1.4 5.5l3.9-3.1z" />
          <path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4C17.9 1.2 15.2 0 12 0 7.4 0 3.4 2.6 1.4 6.5l3.9 3.1c1-2.8 3.6-4.8 6.7-4.8z" />
        </svg>
      )
    case 'microsoft':
      return (
        <svg className="provider-icon" viewBox="0 0 24 24" aria-hidden="true">
          <rect x="1" y="1" width="10.5" height="10.5" fill="#F25022" />
          <rect x="12.5" y="1" width="10.5" height="10.5" fill="#7FBA00" />
          <rect x="1" y="12.5" width="10.5" height="10.5" fill="#00A4EF" />
          <rect x="12.5" y="12.5" width="10.5" height="10.5" fill="#FFB900" />
        </svg>
      )
    default:
      return null
  }
}

export function providerName(provider: string): string {
  switch (provider) {
    case 'google':
      return 'Google'
    case 'microsoft':
      return 'Microsoft'
    default:
      return provider
  }
}
